import { homApi } from '~/redux/apis/hom'
import {
  JoinClassRequest,
  JoinClassResponse,
  JoinCodeResponse,
  ResolveJoinCodeWireResponse,
  SsoCallbackRequest,
  SsoLoginResponse,
  SsoLogoutResponse,
  SsoStartRequest,
  SsoStartResponse,
  StudentLoginRequest,
  StudentLoginResponse,
  TeacherClassesResponse,
  TeacherLoginRequest,
  TeacherLoginResponse,
} from '~/classroom/types/classroom.types'

/**
 * School (b2s) teacher and student access. Deliberately not `~/auth/apis/auth.api`: the school
 * edition does not mount `POST /auth/login`, and each role has its own endpoint that resolves
 * school context server-side.
 *
 * ⚠ The envelope differs per endpoint — do not copy one line to the next. `school-auth.controller`
 * sends `{ type: 'success', data }`; `school-teacher.controller` and `school.controller` send bare
 * objects. Unwrapping an absent envelope silently yields `undefined`, so check each against its
 * controller. F4's envelope is now read off `school-auth.controller.ts`; F2 is still unmounted, so
 * the `{ data }` assumed for it below is unverified.
 */
export const classroomApi = homApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    /** Wrong email and wrong password are indistinguishable, so there is no per-field error. */
    teacherLogin: builder.mutation<TeacherLoginResponse, TeacherLoginRequest>({
      query: (body) => ({ url: '/v1/school/auth/teacher/login', method: 'POST', body }),
      transformResponse: (response: { data: TeacherLoginResponse }) => response.data,
    }),

    /** Rate-limited by submitted username server-side. */
    studentLogin: builder.mutation<StudentLoginResponse, StudentLoginRequest>({
      query: (body) => ({ url: '/v1/school/auth/student/login', method: 'POST', body }),
      // Enveloped, read off `school-auth.controller.ts` rather than copied from its sibling.
      transformResponse: (response: { data: StudentLoginResponse }) => response.data,
    }),

    /**
     * The classes the caller actively teaches. Scoping is entirely server-side, so this sends no
     * filter and must never grow one. Bare response — no `transformResponse`.
     */
    getTeacherClasses: builder.query<TeacherClassesResponse, void>({
      query: () => ({ url: '/v1/school/teacher/classes' }),
    }),

    /**
     * Anonymous. Unknown codes, archived classes and suspended schools are all the same 404 — the
     * screen must not be able to tell which. Bare response: this is the flatten seam for
     * `ResolveJoinCodeWireResponse`, and the only place the server's nesting is known about.
     */
    resolveJoinCode: builder.mutation<JoinCodeResponse, string>({
      query: (code) => ({ url: `/v1/school/join/${encodeURIComponent(code.trim().toUpperCase())}` }),
      transformResponse: (response: ResolveJoinCodeWireResponse) => ({ className: response.class.name }),
    }),

    /**
     * Leg one of single sign-on: hand the server a district slug, get somewhere to send the browser.
     *
     * The authorize URL is built server-side rather than here, matching the existing Google login —
     * it keeps the client id out of the bundle, and the client secret has never been near it.
     */
    startSchoolSso: builder.mutation<SsoStartResponse, SsoStartRequest>({
      query: (body) => ({ url: '/v1/school/auth/sso/start', method: 'POST', body }),
      transformResponse: (response: { data: SsoStartResponse }) => response.data,
    }),

    /**
     * Leg two: exchange the code the provider handed back for our own session.
     *
     * ⚠️ Sends `code` and `state` and nothing else. The server refuses unknown keys, because the
     * district is resolved from the state record it issued — a second way to name a tenant is a way
     * for the two to disagree. Do not add a `slug` here to "help" it.
     */
    completeSchoolSso: builder.mutation<SsoLoginResponse, SsoCallbackRequest>({
      query: (body) => ({ url: '/v1/school/auth/sso/callback', method: 'POST', body }),
      transformResponse: (response: { data: SsoLoginResponse }) => response.data,
    }),

    /**
     * Ask the server where to send the browser so the district's provider ends its session too.
     *
     * ⚠ Must be called while still signed in — the route reads the caller off the verified token.
     * `redirectUrl` is null far more often than it is an error: the hint lives an hour, so anyone
     * signed in longer than that signs out locally, which is the correct outcome.
     */
    schoolSsoLogout: builder.mutation<SsoLogoutResponse, void>({
      query: () => ({ url: '/v1/school/auth/sso/logout', method: 'POST' }),
      transformResponse: (response: { data: SsoLogoutResponse }) => response.data,
    }),

    /**
     * The only way a student account comes into existence. Sends a code and a name only — the
     * server generates the username and password and returns them once.
     */
    joinClass: builder.mutation<JoinClassResponse, JoinClassRequest>({
      query: ({ code, name }) => ({
        url: '/v1/school/auth/student/join',
        method: 'POST',
        body: { code: code.trim().toUpperCase(), name: name.trim() },
      }),
      transformResponse: (response: { data: JoinClassResponse }) => response.data,
    }),
  }),
})

export const {
  useCompleteSchoolSsoMutation,
  useGetTeacherClassesQuery,
  useJoinClassMutation,
  useResolveJoinCodeMutation,
  useSchoolSsoLogoutMutation,
  useStartSchoolSsoMutation,
  useStudentLoginMutation,
  useTeacherLoginMutation,
} = classroomApi
